import { unwrap, type Api } from "../api/index.js";

/**
 * Support cases (W-8 "Need help verifying?"), headless. Region-aware: every
 * call goes through the region's own client (`/api/sg/…` or `/api/hk/…`),
 * never a hardcoded SG path.
 */

export type SupportCaseType =
  | "policy_question"
  | "account_login"
  | "billing_payment"
  | "update_details"
  | "bug_report"
  | "something_else"
  | "identity_verification";

export interface CreateSupportCaseInput {
  caseType: SupportCaseType;
  subject: string;
  message: string;
  /** HK: one of the caller's own ShuftiPro references (else 400). */
  verificationReference?: string;
}

export interface SupportApi {
  /** `GET /support/flags`: is the Support Cases intake on in this region? */
  intakeEnabled(): Promise<boolean>;
  /** `POST /support/cases` with an `Idempotency-Key` header; resolves the case ref (e.g. `HK-0042`). */
  createSupportCase(input: CreateSupportCaseInput, idempotencyKey: string): Promise<{ ref: string }>;
}

export const SUPPORT_SUBJECT_MAX = 200;
export const SUPPORT_MESSAGE_MAX = 10_000;

export function createSupportApi(api: Api): SupportApi {
  const rc = api.regionClient;
  return {
    async intakeEnabled() {
      const r =
        rc.region === "hk"
          ? await unwrap(rc.client.GET("/api/hk/support/flags", {}))
          : await unwrap(rc.client.GET("/api/sg/support/flags", {}));
      return r.support_case_intake === true;
    },
    async createSupportCase(input, idempotencyKey) {
      const body = {
        caseType: input.caseType,
        subject: input.subject.trim(),
        message: input.message.trim(),
        ...(input.verificationReference ? { verificationReference: input.verificationReference } : {}),
      };
      const headers = { "Idempotency-Key": idempotencyKey };
      const r =
        rc.region === "hk"
          ? await unwrap(rc.client.POST("/api/hk/support/cases", { body, headers }))
          : await unwrap(rc.client.POST("/api/sg/support/cases", { body, headers }));
      return { ref: r.ref };
    },
  };
}

export const supportQueryKeys = {
  all: ["support"] as const,
  flags: () => ["support", "flags"] as const,
};

/** Help-form field → i18n error key; empty when valid. */
export function validateHelpMessage(message: string): string | undefined {
  const v = message.trim();
  if (!v) return "verify.hk.help.validation.messageRequired";
  if (v.length > SUPPORT_MESSAGE_MAX) return "verify.hk.help.validation.messageTooLong";
  return undefined;
}
